<template>
  <div class="db-tw-mb-9">
    <div class="db-wrapper-field">
      <div
        ref="dropzoneEl"
        class="db-input db-dropzone"
      >
        <span
          v-if="modelValue.length < MAX_NUMBER_FILES"
          class="dz-message db-tw-max-w-[11em]"
        >
          {{ $t('Drag and drop your files here or') }}
          <span class="db-link">{{ $t('browse') }}</span>
        </span>
        <span
          v-else
          class="dz-message db-tw-max-w-[13em]"
        >
          {{ $t('You have reached the maximum of {x} files per request', { x: MAX_NUMBER_FILES }) }}
        </span>
      </div>

      <label class="db-label">
        <span>{{ label }}</span>
      </label>
    </div>
    <p class="db-input-hint">
      {{ $t('Maximum upload file number: {x}', { x: MAX_NUMBER_FILES }) }} <br />
      {{ $t('Maximum total files size: {x}', { x: filesize(MAX_TOTAL_BYTES, { spacer: '' }) }) }}
    </p>
    <div>
      <DbTagFilter
        class="db-tw-mr-2 db-tw-mt-2"
        v-for="(file, index) in modelValue"
        :key="index"
        v-on:delete="deleteFile(index)"
      >
        {{ file.fileName }} ({{ filesize(file.fileSize) }})
      </DbTagFilter>
    </div>

    <DbModal
      v-if="filesWithError.length"
      class="db-modal-confirmation db-modal-centered-mobile"
      v-on:close-modal="closeErrorModal"
    >
      <template v-slot:header>
        <!--
          /!\ Mandatory for accessibility :
          header slot content is the aria-label of the modal
        -->
        <h1 class="db-visually-hidden">{{ $t('File upload error') }}</h1>
      </template>
      <DbModalConfirmationContent>
        <template v-slot:title>{{ $t('File upload error') }}</template>
        <div class="db-tw-text-left">
          <p>
            {{
              $t('The total files size should not exceed {x}', {
                x: filesize(MAX_TOTAL_BYTES, { spacer: '' }),
              })
            }}
          </p>
          <p>{{ $t('The supported formats are:') }} {{ ACCEPTED_FILES.join(', ') }}</p>
          <p>{{ $t('The following files could not be added:') }}</p>

          <div class="db-wysiwyg db-tw-text-left db-tw-break-words">
            <ul style="word-break: break-all">
              <li
                v-for="(file, index) in filesWithError"
                :key="index"
              >
                {{ file }}
              </li>
            </ul>
          </div>
        </div>

        <template v-slot:buttons>
          <button
            type="button"
            class="db-button-large db-button-secondary"
            v-on:click="closeErrorModal"
          >
            {{ $t('ok') }}
          </button>
        </template>
      </DbModalConfirmationContent>
    </DbModal>
  </div>
</template>
<script lang="ts" setup>
import { ref, onMounted, computed, watch } from 'vue'

import Dropzone from 'dropzone'
import { filesize } from 'filesize'

Dropzone.autoDiscover = false

/* We use only Dropzone for the drag'n'drop (see 'addedfile' event) */

interface RfpFile {
  contentType: string
  fileContent: string
  fileName: string
  fileSize: number
}

const props = withDefaults(
  defineProps<{
    /** binds /w `v-model` directive */
    modelValue?: RfpFile[]
    label: string
  }>(),
  {
    modelValue: () => [],
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', modelValue: RfpFile[]): void
}>()

const dropzoneEl = ref(null)
/** Contains dropzone instance, to be able to enable / disable it */
let myDropzone: Dropzone | undefined

/** We are only allowed to have 5MB of files in total */
const MAX_TOTAL_BYTES = 5000000
const MAX_NUMBER_FILES = 3
const ACCEPTED_FILES = [
  '.txt',
  '.bmp',
  '.jpg',
  '.jpeg',
  '.png',
  '.pdf',
  '.xls',
  '.doc',
  '.docx',
  '.xlsx',
  '.jpg',
  '.msg',
  '.ppt',
  '.pptx',
]

/** Current total size of all files */
const totalSize = computed<number>(() => {
  return props.modelValue.reduce((acc, file) => {
    return acc + file.fileSize
  }, 0)
})

const filesWithError = ref<string[]>([])

/** get base64 of a file using FileReader API  */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      const dataUrl = reader.result

      if (typeof dataUrl !== 'string') {
        reject()
        return
      }

      resolve(dataUrl.split('base64,')[1])
    }
    reader.onerror = (error) => reject(error)
  })
}

function deleteFile(indexToDelete: number) {
  emit(
    'update:modelValue',
    props.modelValue.filter((val, i) => i !== indexToDelete),
  )
}

function addFile(rfpFile: RfpFile) {
  emit('update:modelValue', [...props.modelValue, rfpFile])
}

// If we reach the limit, disable the dropzone
watch(
  () => props.modelValue,
  () => {
    if (!myDropzone) return
    if (props.modelValue.length >= MAX_NUMBER_FILES) {
      myDropzone.disable()
    } else {
      myDropzone.enable()
    }
  },
)

function closeErrorModal() {
  filesWithError.value = []
}

onMounted(() => {
  if (!dropzoneEl.value) {
    return
  }

  myDropzone = new Dropzone(dropzoneEl.value, {
    url: 'FAKE', // this param is required but in our case we don't use it
    autoProcessQueue: false,
    autoQueue: false,
    // @ts-expect-error typescript error because we use Dropzone v6, but ts type is of an older Dropzone
    disablePreviews: true,
    /* this only impacts the "browse" part,
    the event "added" is still fired if someone uses drag'n'drop */
    acceptedFiles: ACCEPTED_FILES.join(','),
  })

  myDropzone.on('addedfile', async (file: File) => {
    try {
      const extension = file.name.split('.').pop()
      if (!extension || !ACCEPTED_FILES?.includes(`.${extension}`)) {
        throw new Error()
      }

      if (props.modelValue.length >= MAX_NUMBER_FILES) {
        throw new Error()
      }

      if (MAX_TOTAL_BYTES - totalSize.value < file.size) {
        throw new Error()
      }

      const base64File = await fileToBase64(file)
      const rfpFile: RfpFile = {
        contentType: file.type,
        fileContent: base64File,
        fileName: file.name,
        fileSize: file.size,
      }

      // to be reviewed, we re-do the same check once again
      // just in case (because we are inside addedfile event callback)
      if (props.modelValue.length >= 3) {
        throw new Error()
      }
      if (MAX_TOTAL_BYTES - totalSize.value < file.size) {
        throw new Error()
      }
      addFile(rfpFile)
    } catch (error) {
      filesWithError.value.push(`${file.name} (${filesize(file.size)})`)
    }
  })
})
</script>

<style scoped>
/* Copy some part of CSS from dropzone to ensure the prefix of CSS class */
/* stylelint-disable */
.db-app .db-dropzone {
  border-style: dashed;

  min-height: 150px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.db-dropzone.dz-clickable {
  cursor: pointer;
}

.db-dropzone:not(.dz-clickable) {
  cursor: not-allowed;
}

.db-dropzone.dz-drag-hover {
  border-style: solid;
}
.db-dropzone.dz-drag-hover .dz-message {
  opacity: 0.5;
}
/* stylelint-disable-enable */
</style>
